import React, { useContext, useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { ThemeContext } from 'styled-components';
import * as s from './styled-chart-load-duration';

const formatNum = (v) => {
  if (!Number.isFinite(v)) return '—';
  if (Math.abs(v) < 100) return v.toFixed(2);
  if (Math.abs(v) < 1000) return v.toFixed(1);
  return Math.round(v).toString();
};

// Curva de duracao de carga (load-duration curve): valores ordenados do
// maior pro menor contra a % do tempo. Le-se "durante X% do tempo a
// variavel ficou >= Y". Classico de geracao/operacao — revela base load,
// picos e quanto tempo opera perto do maximo. Complementa o histograma
// (distribuicao) com a leitura acumulada por tempo.
const MAX_POINTS = 140; // downsample pra plot leve sem perder a forma

const ChartLoadDuration = ({ data, unit, variableName }) => {
  const { colors } = useContext(ThemeContext);

  const curve = useMemo(() => {
    if (!data || data.length === 0) return null;
    const values = data
      .map((p) => Number(p.value))
      .filter((v) => Number.isFinite(v))
      .sort((a, b) => b - a); // desc
    const n = values.length;
    if (!n) return null;

    const target = Math.min(MAX_POINTS, n);
    const points = [];
    for (let i = 0; i < target; i += 1) {
      const frac = target === 1 ? 0 : i / (target - 1);
      const idx = Math.round(frac * (n - 1));
      points.push({ pct: frac * 100, value: values[idx] });
    }
    const max = values[0];
    const min = values[n - 1];
    const mean = values.reduce((acc, v) => acc + v, 0) / n;
    // Fator de carga: media / pico (quao perto da capacidade opera em
    // media). So faz sentido quando o pico e positivo.
    const loadFactor = max > 0 ? (mean / max) * 100 : null;
    return { points, n, max, min, mean, loadFactor };
  }, [data]);

  if (!curve) {
    return (
      <s.Container>
        <s.Header>
          <s.Title>
            Duração de carga
            {variableName && <s.SubTitle>— {variableName}</s.SubTitle>}
          </s.Title>
        </s.Header>
        <s.EmptyMessage>Aguardando dados...</s.EmptyMessage>
      </s.Container>
    );
  }

  return (
    <s.Container>
      <s.Header>
        <s.Title>
          Duração de carga
          {variableName && <s.SubTitle>— {variableName}</s.SubTitle>}
        </s.Title>
        <s.InfoLine>
          <s.InfoItem>
            Pico:{' '}
            <strong>
              {formatNum(curve.max)}
              {unit ? ` ${unit}` : ''}
            </strong>
          </s.InfoItem>
          <s.InfoItem>
            Média:{' '}
            <strong>
              {formatNum(curve.mean)}
              {unit ? ` ${unit}` : ''}
            </strong>
          </s.InfoItem>
          {curve.loadFactor !== null && (
            <s.InfoItem>
              Fator de carga: <strong>{curve.loadFactor.toFixed(0)}%</strong>
            </s.InfoItem>
          )}
        </s.InfoLine>
      </s.Header>
      <s.ChartWrapper>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={curve.points}
            margin={{ top: 8, right: 16, bottom: 8, left: 0 }}
          >
            <defs>
              <linearGradient id="ldc-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={colors.lightblue} stopOpacity={0.5} />
                <stop offset="100%" stopColor={colors.lightblue} stopOpacity={0.04} />
              </linearGradient>
            </defs>
            <CartesianGrid
              stroke={colors.disabled}
              strokeDasharray="3 3"
              opacity={0.4}
            />
            <XAxis
              dataKey="pct"
              type="number"
              domain={[0, 100]}
              tick={{ fill: colors.label, fontSize: 10 }}
              tickLine={false}
              axisLine={{ stroke: colors.disabled, strokeOpacity: 0.5 }}
              tickFormatter={(v) => `${Math.round(v)}%`}
              label={{
                value: '% do tempo',
                position: 'insideBottom',
                fill: colors.label,
                fontSize: 10,
                offset: -2,
              }}
            />
            <YAxis
              tick={{ fill: colors.label, fontSize: 10 }}
              tickLine={false}
              axisLine={false}
              width={48}
              tickFormatter={(v) => formatNum(v)}
            />
            <Tooltip
              contentStyle={{
                background: colors.card,
                border: `1px solid ${colors.disabled}`,
                borderRadius: 8,
                fontSize: 12,
              }}
              labelFormatter={(v) => `${Math.round(v)}% do tempo ≥`}
              formatter={(value) => [
                `${formatNum(value)}${unit ? ` ${unit}` : ''}`,
                'Valor',
              ]}
            />
            {Number.isFinite(curve.mean) && (
              <ReferenceLine
                y={curve.mean}
                stroke={colors.label}
                strokeDasharray="3 6"
                strokeOpacity={0.6}
                label={{
                  value: 'média',
                  position: 'insideTopRight',
                  fill: colors.label,
                  fontSize: 9,
                  fontWeight: 600,
                }}
              />
            )}
            <Area
              type="monotone"
              dataKey="value"
              stroke={colors.lightblue}
              strokeWidth={2}
              fill="url(#ldc-fill)"
              isAnimationActive={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </s.ChartWrapper>
    </s.Container>
  );
};

export default React.memo(ChartLoadDuration);
